/**
 * StackedBarLineChart — dual-axis: STACKED bars (a few segments per column, on the
 * left axis) + a LINE (right axis). Built for the Praxis "Refund Analysis" charts:
 * refund $ stacked by how-fast-it-came-back speed bucket, with the refund count (or
 * rate) as the overlay line. Modeled on ComboChart's restraint (light gridlines,
 * ~12 autoskip x labels, hover tooltip). Segments use a sequential ramp so the
 * fastest→slowest ordering reads as a gradient.
 */

import { useMemo, useRef, useState } from "react";
import { moneyFmt } from "./cards.js";

export interface StackedPoint {
  label: string;
  /** Left-axis stacked segments (same length/order as `segLabels`). */
  segments: number[];
  /** Right-axis overlay value (drawn as the line). */
  line: number;
}

// Light→dark sequential ramp (fast→slow), 7 shades — matches the Praxis blue look.
const RAMP = ["#cfe3ff", "#9dc4fb", "#6ba6f5", "#3f86ea", "#2a6fd0", "#1d5ab0", "#143f80"];

const W = 620;
const H = 248;
const PAD = { l: 52, r: 46, t: 12, b: 28 };
const IW = W - PAD.l - PAD.r;
const IH = H - PAD.t - PAD.b;

export function StackedBarLineChart({
  points,
  segLabels,
  lineLabel,
  barMoney = true,
  lineSuffix = "",
}: {
  points: StackedPoint[];
  /** Legend/tooltip names for each stacked segment (in stack order, bottom→top). */
  segLabels: string[];
  lineLabel: string;
  barMoney?: boolean;
  lineSuffix?: string;
}): React.ReactElement {
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const totals = useMemo(() => points.map((p) => p.segments.reduce((s, v) => s + (v || 0), 0)), [points]);
  const barMax = useMemo(() => Math.max(1, ...totals) * 1.08, [totals]);
  const lineMax = useMemo(() => Math.max(0.0001, ...points.map((p) => p.line || 0)) * 1.12, [points]);

  if (points.length === 0) {
    return <div style={{ color: "var(--faint)", padding: "18px 0" }}>No data yet.</div>;
  }

  const color = (i: number): string => RAMP[Math.round((i / Math.max(1, segLabels.length - 1)) * (RAMP.length - 1))] ?? RAMP[i % RAMP.length]!;
  const barFmt = (v: number): string => (barMoney ? moneyFmt(v) : Math.round(v).toLocaleString());
  const lineFmt = (v: number): string => `${Math.abs(v) >= 100 ? Math.round(v).toLocaleString() : v.toFixed(1)}${lineSuffix}`;

  const xBar = (i: number): number => PAD.l + (i + 0.5) * (IW / points.length);
  const yBar = (v: number): number => PAD.t + IH - (v / barMax) * IH;
  const yLine = (v: number): number => PAD.t + IH - (v / lineMax) * IH;
  const barW = (IW / points.length) * 0.62;
  const gridVals = [0, 0.5, 1];

  const linePts = points
    .map((p, i): [number, number] | null => (Number.isFinite(p.line) ? [xBar(i), yLine(p.line)] : null))
    .filter((q): q is [number, number] => q !== null);
  const linePath = linePts.map((q, i) => `${i ? "L" : "M"}${q[0].toFixed(1)},${q[1].toFixed(1)}`).join(" ");

  const onMove = (e: React.MouseEvent<SVGSVGElement>): void => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const idx = Math.floor((px - PAD.l) / (IW / points.length));
    setHover(Math.max(0, Math.min(points.length - 1, idx)));
  };

  const hovered = hover === null ? null : points[hover] ?? null;
  const step = points.length <= 7 ? 1 : Math.max(1, Math.ceil(points.length / 12));

  return (
    <div style={{ position: "relative" }}>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width="100%" onMouseMove={onMove} onMouseLeave={() => setHover(null)}
        style={{ display: "block", cursor: "crosshair" }} role="img" aria-label={`Stacked ${segLabels.join("/")} bars with ${lineLabel} line`}>
        {gridVals.map((f, i) => {
          const yy = PAD.t + IH - f * IH;
          return (
            <g key={i}>
              <line x1={PAD.l} y1={yy} x2={W - PAD.r} y2={yy} stroke="var(--grid)" />
              <text x={PAD.l - 4} y={yy + 3} textAnchor="end" fill="var(--body-faint)" fontSize={10} className="dd-num">{barFmt(barMax * f)}</text>
              <text x={W - PAD.r + 4} y={yy + 3} textAnchor="start" fill="var(--c2)" fontSize={10} className="dd-num">{lineFmt(lineMax * f)}</text>
            </g>
          );
        })}

        {/* Stacked bars (left axis) — segment by segment, bottom → top */}
        {points.map((p, i) => {
          let acc = 0;
          return (
            <g key={p.label} opacity={hover === null || hover === i ? 1 : 0.55}>
              {p.segments.map((v, s) => {
                const y0 = yBar(acc);
                acc += v || 0;
                const y1 = yBar(acc);
                return <rect key={s} x={xBar(i) - barW / 2} y={y1} width={barW} height={Math.max(0, y0 - y1)} fill={color(s)} />;
              })}
            </g>
          );
        })}

        {/* Overlay line (right axis) + dots */}
        {linePts.length >= 2 && <path d={linePath} fill="none" stroke="var(--c2)" strokeWidth={2.2} strokeLinejoin="round" strokeLinecap="round" />}
        {points.map((p, i) => (Number.isFinite(p.line) ? <circle key={p.label} cx={xBar(i)} cy={yLine(p.line)} r={hover === i ? 3.4 : 2.4} fill="var(--c2)" /> : null))}

        {points.map((p, i) => (i % step === 0 ? <text key={p.label} x={xBar(i)} y={H - 10} textAnchor="middle" fill="var(--body-faint)" fontSize={9}>{p.label}</text> : null))}
        {hover !== null && <line x1={xBar(hover)} y1={PAD.t} x2={xBar(hover)} y2={PAD.t + IH} stroke="var(--body-faint)" strokeDasharray="3 3" />}
      </svg>

      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px", marginTop: 8, fontSize: 10.5, color: "var(--body)" }}>
        {segLabels.map((l, s) => (<span key={l} style={{ display: "inline-flex", alignItems: "center" }}><i style={dot(color(s))} />{l}</span>))}
        <span style={{ display: "inline-flex", alignItems: "center" }}><i style={{ ...dot("var(--c2)"), borderRadius: 9 }} />{lineLabel}</span>
      </div>

      {hovered && hover !== null && (
        <div style={{ position: "absolute", left: `${((xBar(hover) / W) * 100).toFixed(1)}%`, top: 0, transform: `translateX(${hover > points.length / 2 ? "-104%" : "6px"})`, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "var(--shadow-pop)", padding: "8px 11px", fontSize: 11.5, pointerEvents: "none", whiteSpace: "nowrap", zIndex: 5 }}>
          <div style={{ color: "var(--text)", fontWeight: 600, marginBottom: 3 }}>{hovered.label}</div>
          {segLabels.map((l, s) => ((hovered.segments[s] || 0) > 0 ? (
            <div key={l} style={{ color: "var(--body)" }}><i style={dot(color(s))} />{l}: <b className="dd-num" style={{ color: "var(--num)" }}>{barFmt(hovered.segments[s]!)}</b></div>
          ) : null))}
          <div style={{ color: "var(--body)", marginTop: 2, borderTop: "1px solid var(--border)", paddingTop: 2 }}>
            <i style={{ ...dot("var(--c2)"), borderRadius: 9 }} />{lineLabel}: <b className="dd-num" style={{ color: "var(--num)" }}>{lineFmt(hovered.line)}</b>
          </div>
        </div>
      )}
    </div>
  );
}

function dot(bg: string): React.CSSProperties {
  return { display: "inline-block", width: 9, height: 9, borderRadius: 3, marginRight: 5, background: bg, flexShrink: 0 };
}
